import { get } from 'lodash-es';
import { ComputedRef, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { FormTableAction } from '@mts/components/Table';
import {
  buildUUID,
  cloneDeep,
  deepMerge,
  downloadByData,
  downloadByUrl,
  isArray,
  isBoolean,
  isPlainObject,
  message,
  omit,
} from '@mts-fe/utils';
import { t } from '@mts/hooks/web/useI18n';
import { mtsEmitter, SERVICE_PREFIX } from '@mts/utils';
import { downloadFile } from '@mts/utils/file';
import { getTableSettingStore } from '@/store';
import type {
  ActionBtnItem,
  ActionContext,
  PageModalReturnMethods,
  SchemaActionContextProps,
  SchemaPageContextProps,
  SearchTableExposed,
} from '..';
import {
  createApi,
  createSchemaApi,
  getActionHookFn,
  getActionRoute,
  getPrintTemplate,
  getRoutePageMode,
  getTableCacheKey,
  mergeResponse,
  mergeRoutePath,
  parseRouteMethod,
  routerReplace,
} from '..';
import { useHiPrint } from './useHiPrint';

export type ActionHandlerFn = (
  actionEvent: ActionBtnItem,
  record?: any,
  index?: number,
) => Promise<void>;
interface useActionHandlerProps {
  showModal?: PageModalReturnMethods['showModal'];
  pageContext?: SchemaPageContextProps;
  actionContext?: SchemaActionContextProps;
  actionRef?: ComputedRef<any>;
  callback?: ActionHandlerFn;
}

const TABLE_ACTION_EVENT_TYPES = [
  'RECORD_ADD',
  'RECORD_COPY',
  'RECORD_DELETE',
  'MOVE_UP',
  'MOVE_DOWN',
];

/** 按钮事件处理 */
export const useActionHandler = ({
  showModal,
  pageContext,
  actionContext,
  actionRef,
  callback,
}: useActionHandlerProps) => {
  const router = useRouter();
  const context: ActionContext = {
    ...pageContext,
    ...actionContext,
    actionRef,
    message,
    router,
    t,
    createSchemaApi,
  };

  const apiActionHandler: ActionHandlerFn = async (actionEvent, record, index) => {
    console.log('apiActionHandler', { ...actionEvent }, record, index);
    try {
      let data = record || {};
      const {
        checkSelect,
        beforeApiFn = actionContext?.beforeApiFn,
        eventApi,
        afterApiFn = actionContext?.afterApiFn,
        fallbackFn = actionContext?.fallbackFn,
        routeUrl,
        silent,
      } = actionEvent;
      Object.assign(context, { actionEvent, record, index });

      if (checkSelect) {
        const isEmpty = !data || isArray(data) ? !data.length : !Object.keys(data).length;
        if (isEmpty) {
          message.open({ type: 'warning', content: t('g.25090008'), key: 'selectionEmpty' });
          return;
        }
      }

      if (beforeApiFn) {
        const res = await getActionHookFn(beforeApiFn)(data, context, actionEvent?.eventKey);
        if (res !== null && typeof res === 'object') {
          data = res;
        }
        if (typeof res === 'boolean' && !res) return;
      }

      if (actionEvent.modalId && showModal) {
        return showModal(actionEvent, data);
      }

      if (actionEvent.modalCode) {
        const modalProps = context.getBaseModalProps?.(actionEvent.modalCode);
        if (modalProps?.renderMethod) {
          const props = actionEvent.modalPropsModifier?.(modalProps, data) || modalProps;
          return mtsEmitter.emit(modalProps.renderMethod, {
            ...context,
            record: data,
            props,
            onOk:
              actionEvent.afterModalOk ||
              (async (res) => {
                context.actionRef?.value?.reload();
              }),
          });
        }
      }

      if (actionEvent.eventType === 'BASE_EXPORT') {
        if (!actionEvent.exportInModal) {
          const { exportConfig } = actionEvent;
          const exportParams = getTableExportParams(
            actionRef?.value,
            actionEvent,
            data,
            exportConfig?.showSearchFields ?? true,
          );
          await runExportTask(exportParams, eventApi as string, exportConfig?.blob).then(
            (res) => res && !exportConfig?.blob && mtsEmitter.emit('showAsyncTaskRecord', context),
          );
          return;
        } else {
          Object.assign(actionEvent.exportInModal, {
            showSearchFields: actionEvent.exportInModal.showSearchFields ?? true,
          });
          const exportParams = getTableExportParams(
            actionRef?.value,
            actionEvent,
            data,
            actionEvent.exportInModal.showSearchFields,
          );
          return mtsEmitter.emit('startExportTask', { ...context, exportParams });
        }
      }
      if (actionEvent.eventType === 'ASYNC_RECORD') {
        return mtsEmitter.emit('showAsyncTaskRecord', context);
      }
      if (actionEvent.eventType === 'APPROVAL_RECORD') {
        return mtsEmitter.emit('showApprovalRecord', context);
      }

      if (eventApi) {
        if (actionEvent.eventType === 'EXPORT_FILE') {
          actionEvent.flatParam && Object.assign(data, data.search);
          await downloadFile(eventApi as string, data);
          return;
        } else if (actionEvent.eventType === 'TEMPLATE_PRINT') {
          (await useHiPrint(await getPrintTemplate(eventApi as string))).print(data);
        } else {
          const params = {
            modelId: actionEvent.modelId,
            eventKey: actionEvent.eventKey,
            apiParams: { ...actionEvent.apiParams },
          };
          Object.assign(params, actionEvent.flatParam ? data : { data });
          const res = await createSchemaApi(eventApi)?.(params);
          if (res && !isBoolean(res)) {
            if (isPlainObject(res)) {
              data = mergeResponse(data, res, !actionEvent.oldMerge);
            } else if (isArray(res) || typeof res == 'string') {
              data = res;
            } else {
              console.error('未处理的值类型', res);
            }
          }
          !silent && message.success(t('common.opSuccess'));
        }
      }

      if (afterApiFn) {
        await getActionHookFn(afterApiFn)(data, context, actionEvent?.eventKey);
      }

      if (routeUrl) {
        nextTick(() => {
          const mode = getRoutePageMode(actionEvent.eventType);
          const [path, method] = parseRouteMethod(getActionRoute(routeUrl, data));
          if (method === 'go') return router[method](+path);
          if (method === 'replace' && !path) return routerReplace();
          router[method](mode ? mergeRoutePath(router, path, { query: { mode } }) : path);
        });
        return;
      }

      if (fallbackFn) {
        const res = await getActionHookFn(fallbackFn)(data, context, actionEvent?.eventKey);
        if (typeof res !== 'undefined') return;
      }

      callback?.(actionEvent, data, index);
    } catch (error) {
      console.error('apiActionHandler', error);
    }
  };

  const tableActionHandler = async (
    actionEvent: ActionBtnItem,
    record?: any,
    index = 0,
    fallback?: ActionHandlerFn,
  ) => {
    if (
      !(
        TABLE_ACTION_EVENT_TYPES.includes(actionEvent.eventType) &&
        actionRef?.value &&
        'getColumns' in actionRef.value
      )
    ) {
      return false;
    }

    let data = record || {};
    const {
      beforeApiFn = actionContext?.beforeApiFn,
      afterApiFn = actionContext?.afterApiFn,
      fallbackFn = actionContext?.fallbackFn,
      tableActionFn = actionContext?.tableActionFn,
    } = actionEvent;
    Object.assign(context, { actionEvent, record, index });

    if (beforeApiFn) {
      const res = await getActionHookFn(beforeApiFn)(data, context, actionEvent?.eventKey);
      if (res !== null && typeof res === 'object') {
        data = res;
      }
      if (typeof res === 'boolean' && !res) return;
    }

    const tableAction = actionRef.value as FormTableAction;
    const givenRecord =
      tableActionFn && (await getActionHookFn(tableActionFn)(data, context, actionEvent?.eventKey));

    if (actionEvent.eventType === 'RECORD_ADD') {
      const newRecord = givenRecord || getTableDefRecord(tableAction);
      !newRecord.key && Object.assign(newRecord, { key: buildUUID() });
      tableAction.insertTableDataRecord(newRecord, ++index);
    }
    if (actionEvent.eventType === 'RECORD_COPY') {
      const newRecord = omit(cloneDeep(givenRecord || data), ['id', 'key']);
      Object.assign(newRecord, { key: buildUUID() });
      tableAction.insertTableDataRecord(newRecord, ++index);
    }
    if (actionEvent.eventType === 'RECORD_DELETE') {
      const rowKey = (tableAction.getProps().rowKey as string) || 'key';
      tableAction.deleteTableDataRecord(get(givenRecord || data, rowKey));
    }
    if (actionEvent.eventType === 'MOVE_UP' && index !== 0) {
      tableAction.deleteTableDataRecord(data.key);
      tableAction.insertTableDataRecord(data, --index);
    }
    if (actionEvent.eventType === 'MOVE_DOWN') {
      tableAction.insertTableDataRecord(data, index + 2);
      tableAction.deleteTableDataRecord(data.key);
    }

    if (afterApiFn) {
      await getActionHookFn(afterApiFn)(data, context, actionEvent?.eventKey);
    }

    if (fallbackFn) {
      const res = await getActionHookFn(fallbackFn)(data, context, actionEvent?.eventKey);
      if (typeof res !== 'undefined') {
        return;
      }
    }

    fallback?.(actionEvent, data, index);
  };

  const actionHandler: ActionHandlerFn = async (actionEvent, record, index) => {
    const res = await tableActionHandler(actionEvent, record, index);
    return typeof res === 'boolean' && !res ? apiActionHandler(actionEvent, record, index) : void 0;
  };

  return { actionHandler, apiActionHandler, tableActionHandler };
};

/** 根据表格编辑列配置，生成表格行默认数据 */
export const getTableDefRecord = (tableAction: FormTableAction) =>
  tableAction
    .getColumns()
    ?.filter((col) => !!col.editComponent)
    ?.map((col) => col.dataIndex!.toString())
    .reduce((acc, cur) => ({ ...acc, [cur]: '' }), {} as Recordable<string>);

export const getAsyncTaskParams = (actionEvent?: Partial<ActionBtnItem>) => ({
  taskCode: actionEvent?.taskCode?.toString(),
  asyncFlag: !actionEvent?.exportConfig?.syncTask,
  ...actionEvent?.['params'],
});

export const getTableExportParams = (
  tableAction?: FormTableAction | SearchTableExposed,
  actionEvent?: Partial<ActionBtnItem>,
  baseParams?: Recordable,
  useSearchInfo?: boolean,
) => {
  const tableId = tableAction?.getProps()?.tableId;
  console.assert(!!tableId, 'tableId 不存在', tableAction);

  let searchInfo: Recordable = {};
  if (useSearchInfo) {
    searchInfo = omit(tableAction?.getSearchInfo?.(), ['page']);
    actionEvent?.flatParam && Object.assign(searchInfo, searchInfo.search);
  }
  return {
    ...deepMerge(baseParams, searchInfo),
    userColumnConfigDTOList: tableId
      ? getTableSettingStore().getColumns(getTableCacheKey(tableId))
      : null,
    templateCode: actionEvent?.templateCode,
    modelId: actionEvent?.modelId,
    ...getAsyncTaskParams(actionEvent),
  };
};

/** 根据接口执行导出，支持流式下载 */
export const runExportTask = async (
  params: Recordable,
  apiUrl = SERVICE_PREFIX.IRC + '/common/export',
  exportBlob?: boolean,
) => {
  console.assert(
    params.taskCode || params.templateCode || exportBlob,
    '导出任务需要 taskCode templateCode',
  );

  const res = await createApi(
    apiUrl,
    void 0,
    exportBlob ? { responseType: 'blob' } : void 0,
    exportBlob ? { isReturnNativeResponse: true } : void 0,
  )(params);

  if (!exportBlob) {
    if (res.taskStatus === 3 && res.fileUrl) {
      downloadByUrl({ url: res.fileUrl, fileName: res.fileName });
      return;
    }
    return res;
  }

  if (res.status === 200) {
    if (res.data instanceof Blob) {
      const disposition = res.headers.get('content-disposition') as string;
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      const filename =
        matches != null && matches[1] ? decodeURIComponent(matches[1].replace(/['"]/g, '')) : '';
      downloadByData(res.data, filename);
      return;
    }

    try {
      const jsonText = await res.data.text();
      const { error, message: msg } = JSON.parse(jsonText);
      if (error) {
        message.error(msg);
        return;
      }
    } catch (error) {
      console.error(error);
    }
  } else {
    message.error(res.message || t('sys.api.apiRequestFailed'));
  }
};
